import type { ComponentType } from "react";
import { AddGoalDialog, type AddGoalDialogProps } from "@/features/cases";
import { IncomingCallRing, type IncomingCallRingProps } from "@/features/conferencing";
import {
  AddConditionDialog,
  type AddConditionDialogProps,
  AddSymptomDialog,
  type AddSymptomDialogProps,
  AllergyDialog,
  type AllergyDialogProps,
  FlagDialog,
  type FlagDialogProps,
  MedicationDialog,
  type MedicationDialogProps,
} from "@/features/patients";
import { RegisterPatientDialog } from "@/features/patients/components/register-patient-dialog";
import {
  AppointmentDialog,
  type AppointmentDialogProps,
  OrderSetSchedulerDialog,
  type OrderSetSchedulerDialogProps,
} from "@/features/scheduling";
import { CreateTaskDialog, type CreateTaskDialogProps } from "@/features/tasks";

/** What the host supplies to every overlay, on top of that overlay's own props. */
export interface OverlayControlProps {
  open: boolean;
  onClose: () => void;
  /** Fired once the exit animation has finished, so the host can drop the entry. */
  onExited: () => void;
}

/**
 * Props are plain data — never callbacks. The host owns closing, so a stack
 * entry can outlive whatever opened it without holding a stale closure.
 */
export interface OverlayProps {
  "patient.register": Record<string, never>;
  /** Order is significant: sessions are scheduled in the supplied sequence. */
  "order-set.schedule": Omit<OrderSetSchedulerDialogProps, keyof OverlayControlProps>;
  "appointment.book": Omit<AppointmentDialogProps, keyof OverlayControlProps>;
  "patient.flag": Omit<FlagDialogProps, keyof OverlayControlProps>;
  "patient.medication": Omit<MedicationDialogProps, keyof OverlayControlProps>;
  "patient.allergy": Omit<AllergyDialogProps, keyof OverlayControlProps>;
  "patient.condition": Omit<AddConditionDialogProps, keyof OverlayControlProps>;
  "patient.symptom": Omit<AddSymptomDialogProps, keyof OverlayControlProps>;
  "case.goal": Omit<AddGoalDialogProps, keyof OverlayControlProps>;
  "task.create": Omit<CreateTaskDialogProps, keyof OverlayControlProps>;
  /**
   * A colleague is calling. It belongs on this stack and the call surface does
   * not: a ring is a transient notification whose props are plain data, while a
   * room holds a live session and outlives the route that opened it.
   */
  "call.ring": IncomingCallRingProps;
}

export type OverlayKind = keyof OverlayProps;

export const OVERLAY_REGISTRY: {
  [K in OverlayKind]: ComponentType<OverlayProps[K] & OverlayControlProps>;
} = {
  "patient.register": RegisterPatientDialog,
  "appointment.book": AppointmentDialog,
  "order-set.schedule": OrderSetSchedulerDialog,
  "patient.flag": FlagDialog,
  "patient.medication": MedicationDialog,
  "patient.allergy": AllergyDialog,
  "patient.condition": AddConditionDialog,
  "patient.symptom": AddSymptomDialog,
  "case.goal": AddGoalDialog,
  "task.create": CreateTaskDialog,
  "call.ring": IncomingCallRing,
};
